import {
  ComponentType,
  DataType,
  Layout,
} from '@vgtv/api-client/lib/component';

import { getStyleProperty } from '../../utils/getStyleProperty';
import { getComponentItemsCount } from '../../components/PageComponents/components/getComponentItemsCount';

import type { ImageSize } from '../images';
import type { ImagePreset } from './presets';

function processCalc(str: string) {
  const baseFontSize = getStyleProperty('--base-font-size');

  return (
    str
      // nested calc() won't work in iOS, so replace it with ()
      .replace(/calc\(/g, '(')
      // clean up the result string
      .trim()
      .replace(/\s{2,}/g, ' ')
      .replace(/\( /g, '(')
      .replace(/ \)/g, ')')
      // according to https://stackoverflow.com/a/30675128 1rem within the sizes
      // attribute of an image is always equal to the initial font size, regardless
      // of modifications to the font-size on html - so here we read the actual
      // font size in px and replace all rem occurrences with a regular px multiplication
      .replace(/([0-9.]*)rem/g, `($1 * ${baseFontSize})`)
  );
}

/**
 * Returns the intrinsic (physical) width of an image. This is strictly based on
 * custom css variables, and the styling of components.
 * NOTE: This function is memoized, so it only reads everything once - thus window resizing has no effect here.
 */
export const getImageSizes = function (
  imagePreset: ImagePreset,
  size?: ImageSize
) {
  // There's no point in outputting 'sizes' for all media queries, since browsers
  // have inconsistent behavior when viewport is resized (there's no mention of
  // reloading images on resize in the spec).
  // This also makes the logic here much simpler, since we're basing everything
  // on the changes of css variables.

  if (
    imagePreset === 'assetsSlider' ||
    imagePreset === 'assetsSliderVertical' ||
    imagePreset === 'categoriesSlider' ||
    imagePreset === 'profilesSlider'
  ) {
    const containerPadding = getStyleProperty('--content-padding');
    const itemPadding = getStyleProperty('--slider-item-side-padding');

    let dataType = DataType.ASSETS;

    if (imagePreset === 'categoriesSlider') {
      dataType = DataType.CATEGORIES;
    }

    if (imagePreset === 'profilesSlider') {
      dataType = DataType.PROFILES;
    }

    const itemsCount = getComponentItemsCount({
      componentType: ComponentType.SLIDER,
      dataType,
      componentLayout:
        imagePreset === 'assetsSliderVertical'
          ? Layout.VERTICAL
          : Layout.HORIZONTAL,
      size,
    });

    const containerWidth = `(100vw - 2 * ${containerPadding})`;
    const itemWidth = `${containerWidth} / ${itemsCount} - ${itemPadding}`;

    return `calc(${processCalc(itemWidth)})`;
  }

  if (
    imagePreset === 'assetsListVertical' ||
    imagePreset === 'categoriesList'
  ) {
    const containerPadding = getStyleProperty('--list-side-padding');
    const itemPadding = getStyleProperty('--list-item-side-padding');

    const itemsCount = getComponentItemsCount({
      componentType: ComponentType.LIST,
      dataType:
        imagePreset === 'categoriesList'
          ? DataType.CATEGORIES
          : DataType.ASSETS,
      componentLayout:
        imagePreset === 'assetsListVertical'
          ? Layout.VERTICAL
          : Layout.HORIZONTAL,
    });

    const containerWidth = `(100vw - 2 * ${containerPadding})`;
    const itemWidth = `${containerWidth} / ${itemsCount} - ${itemPadding}`;

    return `calc(${processCalc(itemWidth)})`;
  }

  if (
    imagePreset === 'assetsFilteredList' ||
    imagePreset === 'assetsFilteredListVertical'
  ) {
    const containerPadding = getStyleProperty('--list-side-padding');
    const itemPadding = getStyleProperty('--list-item-side-padding');

    const itemsCount = getComponentItemsCount({
      componentType: ComponentType.FILTERED_LIST,
      dataType: DataType.ASSETS,
      componentLayout:
        imagePreset === 'assetsFilteredListVertical'
          ? Layout.VERTICAL
          : Layout.HORIZONTAL,
    });

    const containerWidth = `(100vw - 4 * ${containerPadding})`;
    const itemWidth = `${containerWidth} / ${itemsCount} - ${itemPadding}`;

    return `calc(${processCalc(itemWidth)})`;
  }

  if (imagePreset === 'assetsListHorizontal') {
    const containerPadding = getStyleProperty('--list-side-padding');
    const itemPadding = getStyleProperty('--list-item-side-padding');
    const horizontalAssetImageWidth = getStyleProperty(
      '--horizontal-asset-image-width'
    );

    const itemsCount = getComponentItemsCount({
      componentType: ComponentType.LIST,
      dataType: DataType.ASSETS,
      componentLayout: Layout.HORIZONTAL,
    });

    // asset image is just a % of the item width here
    const imageWidthRatio = parseInt(horizontalAssetImageWidth) / 100;

    const containerWidth = `(100vw - 2 * ${containerPadding})`;
    const itemWidth = `(${containerWidth} / ${itemsCount} - ${itemPadding}) * ${imageWidthRatio}`;

    return `calc(${processCalc(itemWidth)})`;
  }

  if (
    imagePreset === 'editorialSlider' ||
    imagePreset === 'editorialSliderVertical'
  ) {
    const containerPadding = getStyleProperty('--content-padding');
    const itemPadding = getStyleProperty('--slider-item-side-padding');
    const editorialMaxWidth = getStyleProperty('--editorial-max-width');

    const itemsCount = getComponentItemsCount({
      componentType: ComponentType.EDITORIAL,
      dataType: DataType.ASSETS,
      componentLayout:
        imagePreset === 'editorialSliderVertical'
          ? Layout.VERTICAL
          : Layout.HORIZONTAL,
    });

    const containerWidth = `(${editorialMaxWidth} - 2 * ${containerPadding})`;
    const itemWidth = `${containerWidth} / ${itemsCount} - ${itemPadding}`;

    return `calc(${processCalc(itemWidth)})`;
  }

  if (imagePreset === 'editorialProfile') {
    const editorialProfileWidth = getStyleProperty('--editorial-profile-width');

    return `calc(${processCalc(editorialProfileWidth)})`;
  }

  if (imagePreset === 'editorialSingle') {
    const contentPadding = getStyleProperty('--content-padding');
    const editorialMaxWidth = getStyleProperty('--editorial-max-width');

    return `calc(${editorialMaxWidth} - ${contentPadding} * 2)`;
  }

  if (imagePreset === 'categoryLink') {
    const categoryLinkImageWidth = getStyleProperty(
      '--category-link-image-width'
    );

    return `calc(${processCalc(categoryLinkImageWidth)})`;
  }

  if (imagePreset === 'splashBackground') {
    // since this one is always full width
    return `100vw`;
  }

  if (imagePreset === 'splashIcon') {
    const splashIconSize = getStyleProperty('--splash-icon-size');

    return `calc(${processCalc(splashIconSize)})`;
  }
};
